import { useEffect, useRef, useState } from 'react';
import { sessaoAtual } from '../../auth';
import type { Sessao } from '../../auth';
import { Icone } from '../../design/Icone';
import type { Escrita } from '../../giro';
import { lerMeuMural, registrarLeitura } from '../../administracao';
import type { AvisoDoMural } from '../../administracao';
import { CartaoDeAviso, Desfecho } from './comunicacao-comum';
import './mural.css';

/**
 * Os avisos na HOME DO BALCONISTA (tela 30 B): no máximo TRÊS, com "ver todos" → #/mural.
 * Sem aviso, nada aparece — nenhum banner de enfeite. Nenhum aviso comum impede o trabalho: são
 * cartões na página, não um modal. Aparecer aqui conta como visto (o servidor registra).
 */
export function AvisosDaHome() {
  const [avisos, setAvisos] = useState<readonly AvisoDoMural[] | null>(null);
  const [confirmando, setConfirmando] = useState<string | null>(null);
  const [falha, setFalha] = useState<{ id: string; escrita: Escrita<null> } | null>(null);
  const sessao = useRef<Promise<Sessao | null | undefined> | null>(null);
  const daSessao = () => (sessao.current ??= sessaoAtual());

  useEffect(() => {
    let vivo = true;
    void lerMeuMural().then((r) => {
      if (!vivo || !r.ok) return;
      setAvisos(r.dados.avisos);
      const novos = r.dados.avisos.slice(0, 3).filter((a) => !a.vistoEm);
      if (novos.length === 0) return;
      void daSessao().then((s) => {
        if (!s) return;
        for (const a of novos) void registrarLeitura(s, a.id, 'visto');
      });
    });
    return () => {
      vivo = false;
    };
  }, []);

  if (!avisos || avisos.length === 0) return null;

  const confirmar = async (a: AvisoDoMural) => {
    setConfirmando(a.id);
    setFalha(null);
    const s = await daSessao();
    const r: Escrita<null> = s
      ? await registrarLeitura(s, a.id, 'confirmado')
      : { tipo: 'incerta' };
    setConfirmando(null);
    if (r.tipo === 'confirmada')
      setAvisos((l) =>
        (l ?? []).map((x) =>
          x.id === a.id ? { ...x, confirmadoEm: new Date().toISOString() } : x,
        ),
      );
    else setFalha({ id: a.id, escrita: r });
  };

  return (
    <section className="mu-home" aria-label="Avisos">
      <div className="mu-home-lista">
        {avisos.slice(0, 3).map((a) => (
          <CartaoDeAviso
            key={a.id}
            aviso={a}
            resumido
            href="#/mural"
            confirmar={
              a.exigeConfirmacao && !a.confirmadoEm ? (
                <>
                  <button
                    type="button"
                    className="mu-confirmar"
                    disabled={confirmando === a.id}
                    onClick={() => void confirmar(a)}
                  >
                    {confirmando === a.id ? 'Confirmando…' : 'Li e entendi'}
                  </button>
                  {falha?.id === a.id && (
                    <Desfecho
                      escrita={falha.escrita}
                      incerta="Não sabemos se chegou. Toque de novo — confirmar duas vezes não duplica."
                    />
                  )}
                </>
              ) : null
            }
          />
        ))}
      </div>
      <a className="mu-ver-todos" href="#/mural">
        Ver todos os avisos{avisos.length > 3 ? ` (${String(avisos.length)})` : ''}
        <Icone nome="chev-r" tamanho={13} traco={1.8} />
      </a>
    </section>
  );
}
